import { getRetailShopId } from './retailShopChannel'
import { buildStockCategoriesFromApi, mapStockCategoriesToApi } from './safetyStockCategories'

export {
	buildStockCategoriesFromApi,
	cloneStockCategories,
	mapStockCategoriesToApi,
} from './safetyStockCategories'

export const PUSH_CONFIG_TYPES = {
	FIXED_PRICE_CHANNEL: 'fixed_price_channel',
	MANUAL: 'manual',
	SAFETY_STOCK_PRICE: 'safety_stock_price',
}

function centsToYuan(cents) {
	if (cents == null || cents === '') {
		return undefined
	}
	return Number(cents) / 100
}

function yuanToCents(yuan) {
	if (yuan == null || yuan === '') {
		return 0
	}
	return Math.round(Number(yuan) * 100)
}

function mapReturnPolicyFromApi(value) {
	if (value == null || value === '' || value === 0 || value === 'none') {
		return 'none'
	}
	if (value === 1 || value === '1' || value === 'exclude0_100') {
		return 'exclude0_100'
	}
	if (value === 2 || value === '2' || value === 'only100_0') {
		return 'only100_0'
	}
	return 'none'
}

function mapAntiFakeFromApi(value) {
	if (value == null || value === '' || value === 'all') {
		return 'all'
	}
	if (value === 1 || value === '1' || value === 'tear') {
		return 'tear'
	}
	if (value === 0 || value === '0' || value === 'no_tear') {
		return 'no_tear'
	}
	return 'all'
}

function mapChannelScopeFromApi(value) {
	if (value === 2 || value === '2' || value === 'mandatory_backup') {
		return 'mandatory_backup'
	}
	return 'all'
}

function mapProfitTierRow(tier) {
	return {
		priceMin: centsToYuan(tier.min_price),
		priceMax: tier.max_price != null ? centsToYuan(tier.max_price) : undefined,
		profitRate: tier.margin != null ? Number(tier.margin) : undefined,
	}
}

function normalizePushConfigData(responseData) {
	if (!responseData) {
		return {}
	}
	if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
		return responseData.data
	}
	return responseData
}

export function mapManualConfigToForm(data) {
	const src = normalizePushConfigData(data)

	return {
		pre_delivery_date: src.pre_delivery_date != null ? Number(src.pre_delivery_date) : undefined,
		is_enabled: src.is_enabled != null ? Number(src.is_enabled) : undefined,
	}
}

export function mapFixedPriceChannelConfigToForm(data, createTierRow, minTierCount) {
	const minCount = minTierCount || 2
	const src = normalizePushConfigData(data)
	const tiers = Array.isArray(src.profit_tiers) ? src.profit_tiers : []
	let tierList = tiers.map(mapProfitTierRow)

	if (tierList.length === 0) {
		tierList = Array.from({ length: minCount }, createTierRow)
	}

	return {
		grossProfit: src.min_profit_margin != null ? Number(src.min_profit_margin) : undefined,
		nonReturnMarkup: src.no_return_markup != null ? Number(src.no_return_markup) : undefined,
		nonReturnDiscount: src.no_return_discount_threshold != null ? Number(src.no_return_discount_threshold) : undefined,
		pddDeduction: src.deduction_rate != null ? Number(src.deduction_rate) : undefined,
		postage: src.shipping_fee != null ? centsToYuan(src.shipping_fee) : undefined,
		allocationRate: src.allocation_rate_min != null ? Number(src.allocation_rate_min) : undefined,
		onTimeRate: src.on_time_rate_min != null ? Number(src.on_time_rate_min) : undefined,
		shipDays: src.pre_delivery_date != null ? Number(src.pre_delivery_date) : undefined,
		returnPolicy: mapReturnPolicyFromApi(src.return_policy),
		securityStatus: mapAntiFakeFromApi(src.anti_fake_status),
		channelSet: mapChannelScopeFromApi(src.channel_scope),
		is_enabled: src.is_enabled != null ? Number(src.is_enabled) : undefined,
		tierList: tierList,
	}
}

export function mapSafetyStockPriceConfigToForm(data, createTierRow, minTierCount) {
	const src = normalizePushConfigData(data)
	const formFields = mapFixedPriceChannelConfigToForm(src, createTierRow, minTierCount)

	return {
		form: Object.assign({}, formFields, {
			singleBuyMarkup: src.price_add != null ? Number(src.price_add) : undefined,
			priceProtectDays: src.un_price_down_day != null ? Number(src.un_price_down_day) : undefined,
			priceUpInRange: src.price_change_up != null ? Number(src.price_change_up) : undefined,
			priceDownInRange: src.price_change_down != null ? Number(src.price_change_down) : undefined,
			priceUpOutRange: src.price_change_up2 != null ? Number(src.price_change_up2) : undefined,
			priceDownOutRange: src.price_change_down2 != null ? Number(src.price_change_down2) : undefined,
		}),
		stockCategories: buildStockCategoriesFromApi(src.safety_stocks || src.stock_configs),
	}
}

function isTierRowFilled(row) {
	if (!row) {
		return false
	}
	return (row.priceMin != null && row.priceMin !== '')
		|| (row.priceMax != null && row.priceMax !== '')
		|| (row.profitRate != null && row.profitRate !== '')
}

export function validateProfitTierList(tierList) {
	const list = tierList || []
	for (let i = 0; i < list.length; i++) {
		const row = list[i]
		if (!isTierRowFilled(row)) {
			continue
		}
		const min = row.priceMin
		const max = row.priceMax
		if (min != null && min !== '' && max != null && max !== '') {
			if (Number(min) > Number(max)) {
				return {
					valid: false,
					message: '阶梯毛利润配置第' + (i + 1) + '条：最低价不能大于最高价',
				}
			}
		}
	}
	return { valid: true, message: '' }
}

function mapProfitTierToApi(row) {
	return {
		min_price: yuanToCents(row.priceMin),
		max_price: row.priceMax != null && row.priceMax !== '' ? yuanToCents(row.priceMax) : null,
		margin: Number(row.profitRate),
	}
}

export function buildManualSavePayload(retailShopId, form) {
	return {
		retail_shop_id: Number(retailShopId),
		type: PUSH_CONFIG_TYPES.MANUAL,
		data: {
			is_enabled: form.is_enabled != null ? Number(form.is_enabled) : 1,
			pre_delivery_date: Number(form.pre_delivery_date),
		},
	}
}

export function buildFixedPriceChannelSavePayload(retailShopId, form) {
	const tierValidation = validateProfitTierList(form.tierList)
	if (!tierValidation.valid) {
		throw new Error(tierValidation.message)
	}
	const tierList = form.tierList || []
	const profitTiers = tierList.filter(isTierRowFilled).map(mapProfitTierToApi)

	return {
		retail_shop_id: Number(retailShopId),
		type: PUSH_CONFIG_TYPES.FIXED_PRICE_CHANNEL,
		data: {
			is_enabled: form.is_enabled != null ? Number(form.is_enabled) : 1,
			min_profit_margin: Number(form.grossProfit),
			no_return_markup: Number(form.nonReturnMarkup),
			no_return_discount_threshold: Number(form.nonReturnDiscount),
			deduction_rate: Number(form.pddDeduction),
			shipping_fee: yuanToCents(form.postage),
			return_policy: form.returnPolicy,
			allocation_rate_min: Number(form.allocationRate),
			on_time_rate_min: Number(form.onTimeRate),
			pre_delivery_date: Number(form.shipDays),
			anti_fake_status: form.securityStatus,
			channel_scope: form.channelSet,
			profit_tiers: profitTiers,
		},
	}
}

export function buildSafetyStockPriceSavePayload(retailShopId, form, stockCategories) {
	const fixedPayload = buildFixedPriceChannelSavePayload(retailShopId, form)

	const data = Object.assign({}, fixedPayload.data, {
		price_add: Number(form.singleBuyMarkup),
		un_price_down_day: Number(form.priceProtectDays),
		price_change_up: Number(form.priceUpInRange),
		price_change_down: Number(form.priceDownInRange),
		price_change_up2: Number(form.priceUpOutRange),
		price_change_down2: Number(form.priceDownOutRange),
	})
	if (stockCategories != null) {
		data.safety_stocks = mapStockCategoriesToApi(stockCategories)
	}

	return {
		retail_shop_id: fixedPayload.retail_shop_id,
		type: PUSH_CONFIG_TYPES.SAFETY_STOCK_PRICE,
		data: data,
	}
}

export function buildGoodsFixedPriceChannelSavePayload(productId, form) {
	const payload = buildFixedPriceChannelSavePayload(0, form)
	return {
		retail_shop_product_id: Number(productId),
		type: payload.type,
		data: payload.data,
	}
}

export function buildGoodsSafetyStockPriceSavePayload(productId, form, stockCategories) {
	const payload = buildSafetyStockPriceSavePayload(0, form, stockCategories)
	return {
		retail_shop_product_id: Number(productId),
		type: payload.type,
		data: payload.data,
	}
}

export function savePushConfig(axios, payload) {
	return axios.post('api/retail_shop/push_config_save', payload).then(function(res) {
		if (res.data.code != 0) {
			throw new Error(res.data.message || '保存失败')
		}
		return res.data
	})
}

export function fetchPushConfig(axios, retailShopId, type) {
	return axios.post('api/retail_shop/push_config_get', {
		retail_shop_id: retailShopId,
		type: type,
	}).then(function(res) {
		if (res.data.code != 0) {
			throw new Error(res.data.message || '获取配置失败')
		}
		return normalizePushConfigData(res.data.data)
	})
}

export function fetchGoodsPushConfig(axios, productId, type) {
	return axios.post('api/retail_goods/push_config_get', {
		retail_shop_product_id: productId,
		type: type,
	}).then(function(res) {
		if (res.data.code != 0) {
			throw new Error(res.data.message || '获取配置失败')
		}
		return normalizePushConfigData(res.data.data)
	})
}

export function saveGoodsPushConfig(axios, payload) {
	return axios.post('api/retail_goods/push_config_save', payload).then(function(res) {
		if (res.data.code != 0) {
			throw new Error(res.data.message || '保存失败')
		}
		return res.data
	})
}

export function isProductPushConfigScope(retailShop) {
	return !!(retailShop && retailShop.pushConfigScope === 'product' && retailShop.retailShopProductId)
}

export function getProductPushConfigId(retailShop) {
	if (!isProductPushConfigScope(retailShop)) {
		return ''
	}
	return retailShop.retailShopProductId
}

export { getRetailShopId }



// WEBPACK FOOTER //
// ./src/components/StoreConfig/detailed/retailShopPushConfig.js